import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axios';
import CreateCustomer from './CreateCustomer';
import TrialBalance from './TrialBalance';
import CreateJournalEntry from './CreateJournalEntry';

const Icon = ({ d }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

function Dashboard() {
  const [userEmail, setUserEmail] = useState('');
  const [isEmailSet, setIsEmailSet] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [activeTab, setActiveTab] = useState('trialBalance');

  useEffect(() => {
    const storedEmail = localStorage.getItem('userEmail');
    if (storedEmail) {
      setUserEmail(storedEmail);
      setIsEmailSet(true);
      checkConnectionStatus(storedEmail);
    }
  }, []);

  const checkConnectionStatus = async (email) => {
    try {
      const response = await axios.get(`/auth/status?user_id=${encodeURIComponent(email)}`);
      setIsConnected(response.data.connected);
      setConnectionStatus(response.data);
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (userEmail.trim()) {
      localStorage.setItem('userEmail', userEmail);
      setIsEmailSet(true);
      checkConnectionStatus(userEmail);
    }
  };

  const handleChangeEmail = () => {
    setIsEmailSet(false);
    setIsConnected(false);
    setConnectionStatus(null);
    setUserEmail('');
    localStorage.removeItem('userEmail');
  };

  const connectQuickBooks = async () => {
    try {
      const response = await axios.get(`/auth/connect?user_id=${encodeURIComponent(userEmail)}`);
      const authUrl = response.data.authUrl;
      
      window.open(authUrl, '_blank', 'width=800,height=600');
      
      const checkInterval = setInterval(async () => {
        const status = await axios.get(`/auth/status?user_id=${encodeURIComponent(userEmail)}`);
        if (status.data.connected) {
          setIsConnected(true);
          setConnectionStatus(status.data);
          clearInterval(checkInterval);
        }
      }, 2000);
    } catch (error) {
      console.error('Error connecting to QuickBooks:', error);
      alert('Failed to connect to QuickBooks.');
    }
  };

  const disconnectQuickBooks = async () => {
    try {
      await axios.post(`/auth/disconnect?user_id=${encodeURIComponent(userEmail)}`);
      setIsConnected(false);
      setConnectionStatus(null);
      setCompanyInfo(null);
      setCustomers([]);
      setInvoices([]);
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const handleCustomerCreated = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setShowCreateCustomer(false);
  };

  if (!isEmailSet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-soft to-white">
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-line">
          <div className="max-w-[1560px] mx-auto px-6 h-16 flex items-center justify-between">
            <Link className="flex items-center gap-2.5 font-bold" to="/">
              <span className="w-[30px] h-[30px] rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-[13px] font-extrabold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]">Q</span>
              <span>QuickBook Connector</span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 h-10 px-4 rounded-[10px] font-semibold text-sm text-muted hover:text-ink hover:bg-soft transition-all">
              ← Back to Home
            </Link>
          </div>
        </header>

        <div className="max-w-[480px] mx-auto px-6 py-20">
          <div className="bg-white rounded-2xl shadow-custom border border-line p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-2xl font-extrabold mx-auto mb-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]">Q</div>
              <h1 className="text-2xl font-bold text-ink mb-2">Welcome to Dashboard</h1>
              <p className="text-muted text-sm">Enter your email to access QuickBooks integration</p>
            </div>
            
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-2">Email Address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  required
                  className="w-full h-12 px-4 border border-line rounded-xl text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo focus:border-transparent transition-all"
                />
              </div>
              <button 
                type="submit" 
                className="w-full h-12 bg-navy text-white rounded-xl font-semibold hover:bg-navy-2 hover:-translate-y-0.5 transition-all shadow-sm"
              >
                Continue to Dashboard
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft to-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-line">
        <div className="max-w-[1560px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link className="flex items-center gap-2.5 font-bold" to="/">
            <span className="w-[30px] h-[30px] rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-[13px] font-extrabold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]">Q</span>
            <span>QuickBooks OAuth Server</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-soft rounded-lg border border-line">
              <Icon d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <span className="text-sm font-medium text-ink">{userEmail}</span>
            </div>
            <button 
              onClick={handleChangeEmail}
              className="text-sm font-medium text-muted hover:text-ink px-3 py-2 rounded-lg hover:bg-soft transition-all"
            >
              Change Email
            </button>
            <Link to="/" className="inline-flex items-center gap-2 h-10 px-4 rounded-[10px] font-semibold text-sm text-muted hover:text-ink hover:bg-soft transition-all">
              ← Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1560px] mx-auto px-6 py-8">
        {/* Connection Status Card */}
        <div className="bg-white rounded-2xl shadow-custom border border-line p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-bold text-ink mb-1">QuickBooks Connection</h2>
              {isConnected && connectionStatus?.organizations?.length > 0 ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <p className="text-sm font-medium text-green-600">Connected</p>
                  </div>
                  <p className="text-sm text-muted">Company: <span className="font-medium text-ink">{connectionStatus.organizations[0].companyName}</span></p>
                  <p className="text-xs text-muted">Realm ID: {connectionStatus.organizations[0].realmId}</p>
                  <p className="text-xs text-muted">Expires: {new Date(connectionStatus.organizations[0].expiresAt).toLocaleString()}</p>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  <p className="text-sm font-medium text-red-600">Not Connected</p>
                </div>
              )}
            </div>
            <div>
              {isConnected ? (
                <button 
                  onClick={disconnectQuickBooks}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-xl font-semibold text-sm bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all"
                >
                  <Icon d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9" />
                  Disconnect
                </button>
              ) : (
                <button 
                  onClick={connectQuickBooks}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-xl font-semibold text-sm bg-navy text-white hover:bg-navy-2 hover:-translate-y-0.5 transition-all"
                >
                  <Icon d="M13 10V3L4 14h7v7l9-11h-7z" />
                  Connect to QuickBooks
                </button>
              )}
            </div>
          </div>
        </div>

        {isConnected && (
          <>
            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <button 
                onClick={() => setActiveTab('trialBalance')}
                className={`p-5 rounded-xl border-2 transition-all text-left ${
                  activeTab === 'trialBalance' 
                    ? 'border-indigo-2 bg-indigo-2/5' 
                    : 'border-line bg-white hover:border-indigo-2/50'
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#eef0ff] to-[#e0e4ff] grid place-items-center text-navy mb-3">
                  <Icon d="M9 11l3 3L22 4 M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </div>
                <h3 className="font-semibold text-ink mb-1">Trial Balance</h3>
                <p className="text-xs text-muted">View account balances</p>
              </button>

              <button 
                onClick={() => setActiveTab('createJournal')}
                className={`p-5 rounded-xl border-2 transition-all text-left ${
                  activeTab === 'createJournal' 
                    ? 'border-indigo-2 bg-indigo-2/5' 
                    : 'border-line bg-white hover:border-indigo-2/50'
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#eef0ff] to-[#e0e4ff] grid place-items-center text-navy mb-3">
                  <Icon d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </div>
                <h3 className="font-semibold text-ink mb-1">Journal Entry</h3>
                <p className="text-xs text-muted">Create new entries</p>
              </button>

              <button 
                onClick={() => setShowCreateCustomer(!showCreateCustomer)}
                className="p-5 rounded-xl border-2 border-line bg-white hover:border-indigo-2/50 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#eef0ff] to-[#e0e4ff] grid place-items-center text-navy mb-3">
                  <Icon d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M12.5 7a4 4 0 100-8 4 4 0 000 8z M20 8v6 M23 11h-6" />
                </div>
                <h3 className="font-semibold text-ink mb-1">Add Customer</h3>
                <p className="text-xs text-muted">Create new customer</p>
              </button>

              <a 
                href="https://qbo1.api.xcelerateai-api.com/api-docs/"
                target="_blank"
                rel="noreferrer"
                className="p-5 rounded-xl border-2 border-line bg-white hover:border-indigo-2/50 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#eef0ff] to-[#e0e4ff] grid place-items-center text-navy mb-3">
                  <Icon d="M4 19.5A2.5 2.5 0 016.5 17H20 M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                </div>
                <h3 className="font-semibold text-ink mb-1">API Docs</h3>
                <p className="text-xs text-muted">View documentation</p>
              </a>
            </div>

            {/* Main Content Area */}
            <div className="bg-white rounded-2xl shadow-custom border border-line overflow-hidden">
              <div className="border-b border-line bg-soft px-6 py-4">
                <div className="flex items-center gap-4 overflow-x-auto">
                  <button 
                    onClick={() => setActiveTab('trialBalance')}
                    className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                      activeTab === 'trialBalance' 
                        ? 'bg-white text-indigo-2 shadow-sm' 
                        : 'text-muted hover:text-ink hover:bg-white/50'
                    }`}
                  >
                    ⚖️ Trial Balance
                  </button>
                  <button 
                    onClick={() => setActiveTab('createJournal')}
                    className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                      activeTab === 'createJournal' 
                        ? 'bg-white text-indigo-2 shadow-sm' 
                        : 'text-muted hover:text-ink hover:bg-white/50'
                    }`}
                  >
                    📝 Create Journal Entry
                  </button>
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'trialBalance' && <TrialBalance />}
                {activeTab === 'createJournal' && <CreateJournalEntry />}
              </div>
            </div>

            {/* Create Customer Modal/Section */}
            {showCreateCustomer && (
              <div className="mt-6">
                <CreateCustomer onCustomerCreated={handleCustomerCreated} />
              </div>
            )}

            {/* Additional Data Display */}
            {companyInfo && (
              <div className="mt-6 bg-white rounded-2xl shadow-custom border border-line p-6">
                <h3 className="text-lg font-bold text-ink mb-4">Company Information</h3>
                <pre className="bg-soft p-4 rounded-lg overflow-auto text-xs font-mono text-ink border border-line">
                  {JSON.stringify(companyInfo, null, 2)}
                </pre>
              </div>
            )}

            {customers.length > 0 && (
              <div className="mt-6 bg-white rounded-2xl shadow-custom border border-line overflow-hidden">
                <div className="px-6 py-4 border-b border-line">
                  <h3 className="text-lg font-bold text-ink">Customers ({customers.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-soft">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wider">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {customers.map((customer) => (
                        <tr key={customer.Id} className="hover:bg-soft/50 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-ink">{customer.DisplayName}</td>
                          <td className="px-6 py-4 text-sm text-muted">{customer.PrimaryEmailAddr?.Address || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm font-medium text-ink">${customer.Balance || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
